import { Card, CardContent } from "../../ui/card";
import { cn } from "../../utils/common/cn";
import type { ScenarioCodeTile } from "../../utils/portfolio-risk-concentration/api/scenario";
import { usd } from "./format";

/** The contract count shown in brackets after a dollar figure: "(1,234)". Nothing when the server did not send one. */
function Count({ n }: { n: number | undefined }) {
  return n === undefined ? null : (
    <span className="ml-1.5 text-xs font-normal text-muted-foreground">({n.toLocaleString("en-US")})</span>
  );
}

/**
 * One card per facility (investor code) the scenario touches or that holds active contracts: its name, and what it holds today (Actual)
 * beside what it holds once the scenario is applied (Scenario), in one row, with the change under them. Shared by Scenario Monitor and
 * Scenario Capacity. A figure the chosen basis does not measure is a dash.
 */
export function ScenarioFacilityCards({ tiles, basisLabel }: { tiles: ScenarioCodeTile[]; basisLabel?: string }) {
  if (tiles.length === 0) return null;
  return (
    <>
    {/* The figures follow the page's "Measured in" choice, which is not the book value the earlier Scenario steps show: say so. */}
    {basisLabel ? <p className="mt-2 text-[11px] text-muted-foreground">Amounts below are measured in {basisLabel}.</p> : null}
    <div className="mt-2 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
      {tiles.map((t) => {
        const change = t.currentDollars === null || t.scenarioDollars === null ? null : t.scenarioDollars - t.currentDollars;
        const name = t.name && t.name !== `Code ${t.code}` ? t.name : `Code ${t.code}`;
        return (
          <Card key={t.code}>
            <CardContent className="py-4">
              <p className="truncate text-sm font-semibold text-foreground" title={name}>
                {name}
              </p>
              {name !== `Code ${t.code}` && <p className="text-[11px] text-muted-foreground">Code {t.code}</p>}
              <div className="mt-3 grid grid-cols-2 gap-3">
                <div>
                  <p className="text-[11px] text-muted-foreground">Actual</p>
                  <p className="mt-0.5 text-base font-semibold tabular-nums">
                    {t.currentDollars === null ? "—" : usd(t.currentDollars)}
                    <Count n={t.activeContracts} />
                  </p>
                </div>
                <div>
                  <p className="text-[11px] text-muted-foreground">Scenario</p>
                  <p className="mt-0.5 text-base font-semibold tabular-nums text-amber">
                    {t.scenarioDollars === null ? "—" : usd(t.scenarioDollars)}
                    <Count n={t.scenarioContracts} />
                  </p>
                </div>
              </div>
              {change !== null && change !== 0 && (
                <p className={cn("mt-1.5 text-[11px] font-medium", change > 0 ? "text-[color:var(--approve)]" : "text-destructive")}>
                  {change > 0 ? "+" : "−"}
                  {usd(Math.abs(change))}
                </p>
              )}
            </CardContent>
          </Card>
        );
      })}
    </div>
    </>
  );
}
